import type {
  AutoAttendantNodeData,
  CallDistributionNodeData,
  DayNightNodeData,
  InteractiveWebhookNodeData,
  QueueNodeData,
  ScheduleNodeData,
  SwitchboardEdge,
  SwitchboardNode,
} from '../shared/reactFlowTypes'
import { NodeType } from '../shared/reactFlowTypes'
import { extractNodeIdFromUuid } from '../shared/uuidUtils'
import type { LegacyFormatNode } from './index'

type LegacySourceEndpoint = NonNullable<
  NonNullable<LegacyFormatNode['endpoints']>['sources']
>[number]

function extractOptionIdFromOptionSourceUuid(
  sourceUuid: unknown,
): string | undefined {
  if (typeof sourceUuid !== 'string' || !sourceUuid.endsWith('_src')) {
    return undefined
  }

  const optionId = sourceUuid.slice(0, -4)
  return optionId.includes('_option-') ? optionId : undefined
}

/**
 * Convert legacy endpoints to React Flow edges
 *
 * @param oldNodes - Array of legacy format nodes with endpoints
 * @param nodeMap - Map of node IDs to React Flow nodes
 * @returns Array of React Flow edges created from legacy endpoints
 */
export const convertLegacyEndpointsToReactFlowEdges = (
  oldNodes: LegacyFormatNode[],
  nodeMap: Map<string, SwitchboardNode>,
): SwitchboardEdge[] => {
  const edges: SwitchboardEdge[] = []
  const edgeIdSet = new Set<string>()

  const preserveSourceEndpointForRoundTrip = (
    legacyNode: LegacyFormatNode,
    source: LegacySourceEndpoint,
  ): void => {
    if (!source.uuid) {
      return
    }

    const node = nodeMap.get(legacyNode.id)
    if (!node?.data) {
      return
    }

    if (!Array.isArray(node.data.unconnectedSourceEndpoints)) {
      node.data.unconnectedSourceEndpoints = []
    }

    const preservedTargetUuid =
      typeof source.target_uuid === 'string' ? source.target_uuid : null
    const unconnectedSourceEndpoints = node.data
      .unconnectedSourceEndpoints as Array<Record<string, unknown>>
    const existing = unconnectedSourceEndpoints.find(
      (endpoint) => endpoint.uuid === source.uuid,
    )

    if (existing) {
      if (
        preservedTargetUuid &&
        (existing.target_uuid === null || existing.target_uuid === undefined)
      ) {
        existing.target_uuid = preservedTargetUuid
      }
      if (!existing.option_id && source.option_id) {
        existing.option_id = source.option_id
      }
      return
    }

    const unconnectedEndpoint: Record<string, unknown> = {
      uuid: source.uuid,
      anchor: source.anchor || 'Right',
      my_label: source.my_label || 'Drag',
      option_id: source.option_id || undefined,
    }
    if (preservedTargetUuid) {
      unconnectedEndpoint.target_uuid = preservedTargetUuid
    }
    unconnectedSourceEndpoints.push(unconnectedEndpoint)
  }

  const targetEndpointUuidToNode = new Map<string, LegacyFormatNode>()
  oldNodes.forEach((oldNode) => {
    // CRITICAL: Ensure targets is always an array before calling array methods
    // Legacy format may have targets as undefined/null or malformed data
    const targets = Array.isArray(oldNode.endpoints?.targets)
      ? oldNode.endpoints.targets
      : []
    if (targets.length > 0) {
      const validTargets = targets.filter(
        (target) => target && Object.keys(target).length > 0 && target.uuid,
      )
      validTargets.forEach((target) => {
        if (target.uuid) {
          targetEndpointUuidToNode.set(target.uuid, oldNode)
        }

        // CRITICAL: Preserve unconnected target endpoints (source_uuid: null) in node data
        // This ensures round-trip compatibility - unconnected target endpoints must be preserved
        if (!target.source_uuid) {
          const node = nodeMap.get(oldNode.id)
          if (node && node.data) {
            // Initialize unconnectedTargetEndpoints array if it doesn't exist
            if (!Array.isArray(node.data.unconnectedTargetEndpoints)) {
              node.data.unconnectedTargetEndpoints = []
            }

            // Store unconnected target endpoint metadata
            const unconnectedTargetEndpoint = {
              uuid: target.uuid,
              anchor: target.anchor || 'Left',
              my_label: target.my_label || 'Drag',
            }

            // Only add if not already present (avoid duplicates)
            const existing = (
              node.data.unconnectedTargetEndpoints as Array<
                Record<string, unknown>
              >
            ).find((ep) => ep.uuid === target.uuid)
            if (!existing) {
              ;(
                node.data.unconnectedTargetEndpoints as Array<
                  Record<string, unknown>
                >
              ).push(unconnectedTargetEndpoint)
            }
          }
        }
      })
    }
  })

  oldNodes.forEach((oldNode) => {
    // CRITICAL: Ensure sources is always an array before calling array methods
    // Legacy format may have sources as {} (empty object) for direct_line nodes,
    // or it may be undefined/null. Always ensure it's an array before iteration.
    const sources = Array.isArray(oldNode.endpoints?.sources)
      ? oldNode.endpoints.sources
      : []
    if (sources.length === 0) return
    const validSources = sources.filter(
      (source) => source && Object.keys(source).length > 0,
    )

    validSources.forEach((source) => {
      // CRITICAL: Preserve unconnected endpoints (target_uuid: null) in node data
      // This ensures round-trip compatibility - unconnected endpoints must be preserved
      if (!source.uuid) {
        return // Skip if UUID is missing (invalid endpoint)
      }

      // If endpoint is unconnected (target_uuid: null), store it in node data for later recreation
      if (!source.target_uuid) {
        preserveSourceEndpointForRoundTrip(oldNode, source)
        return // Skip creating edge for unconnected endpoint
      }

      const targetNodeWithEndpoint = targetEndpointUuidToNode.get(
        source.target_uuid,
      )

      let targetNodeId: string | undefined

      // Prefer exact endpoint UUID match first. This supports legacy payloads with
      // non-standard endpoint UUIDs that do not encode a numeric node id.
      if (targetNodeWithEndpoint) {
        const endpointNodeId = targetNodeWithEndpoint.id
        if (nodeMap.has(endpointNodeId)) {
          targetNodeId = endpointNodeId
        }
      }

      // Fall back to numeric extraction when endpoint UUID lookup is unavailable.
      if (!targetNodeId) {
        const extractedNodeId = extractNodeIdFromUuid(source.target_uuid)
        if (extractedNodeId) {
          for (const [nodeId] of nodeMap.entries()) {
            const nodeNumericId = nodeId.includes('-')
              ? nodeId.split('-').pop() || nodeId
              : nodeId
            if (nodeNumericId === extractedNodeId) {
              targetNodeId = nodeId
              break
            }
          }

          if (!targetNodeId) {
            targetNodeId = extractedNodeId
          }
        }
      }

      if (!targetNodeId) {
        preserveSourceEndpointForRoundTrip(oldNode, source)
        return
      }

      if (!targetNodeWithEndpoint && targetNodeId) {
        console.warn(
          `[Legacy Conversion] Missing target endpoint UUID but target node exists:`,
          {
            sourceNodeId: oldNode.id,
            sourceHandleLabel: source.my_label || 'unknown',
            targetNodeId: targetNodeId,
            missingTargetEndpointUuid: source.target_uuid,
            message:
              'Legacy bug: target endpoint UUID referenced by source but not found in target node targets array',
          },
        )
      }

      const myLabel = source.my_label || ''
      const anchor = source.anchor || 'Right'
      const sourceOptionId =
        source.option_id || extractOptionIdFromOptionSourceUuid(source.uuid)

      if (
        oldNode.kind === 'auto_attendant' &&
        isOnInvalidLabel(myLabel) &&
        source.target_uuid
      ) {
        // CRITICAL: Ensure targets is always an array before calling array methods
        const targets = Array.isArray(oldNode.endpoints?.targets)
          ? oldNode.endpoints.targets
          : []
        if (targets.some((target) => target.uuid === source.target_uuid)) {
          targetNodeId = oldNode.id
        }
      }

      const isDynamicOptionSelfLoop =
        (oldNode.kind === 'auto_attendant' ||
          oldNode.kind === 'interactive_webhook' ||
          oldNode.kind === 'call_distribution') &&
        anchor === 'Right' &&
        !isOnInvalidLabel(myLabel) &&
        !isOnTimeoutLabel(myLabel) &&
        (!!sourceOptionId || myLabel.startsWith('option-'))

      const isSelfLoopAllowed =
        (oldNode.kind === 'auto_attendant' &&
          (isOnInvalidLabel(myLabel) || isOnTimeoutLabel(myLabel))) ||
        isDynamicOptionSelfLoop ||
        false

      if (targetNodeId && (oldNode.id !== targetNodeId || isSelfLoopAllowed)) {
        const sourceNode = nodeMap.get(oldNode.id)
        const targetNode = nodeMap.get(targetNodeId)

        const actualTargetNodeId =
          isSelfLoopAllowed && oldNode.id === targetNodeId
            ? oldNode.id
            : targetNodeId
        const actualTargetNode =
          isSelfLoopAllowed && oldNode.id === targetNodeId
            ? sourceNode
            : targetNode

        if (sourceNode && actualTargetNode) {
          if (
            (oldNode.kind === 'auto_attendant' ||
              oldNode.kind === 'interactive_webhook') &&
            anchor === 'Right' &&
            !isOnInvalidLabel(myLabel) &&
            !isOnTimeoutLabel(myLabel)
          ) {
            if (!hasMenuOptions(oldNode)) {
              return
            }
          }

          const isCustomHandleLowercase =
            myLabel.startsWith('option-') || CUSTOM_HANDLE_LABELS.has(myLabel)

          const edgeTargetId = actualTargetNodeId

          let inferredSourceHandle: string | undefined
          if (
            oldNode.kind === 'call_distribution' &&
            !isCustomHandleLowercase &&
            anchor === 'Right'
          ) {
            let matchedOptionIndex: number | undefined
            const sourceOptionId =
              source.option_id ||
              extractOptionIdFromOptionSourceUuid(source.uuid)

            // CRITICAL: Try to match by option_id first (if preserved)
            if (sourceOptionId) {
              matchedOptionIndex = findOptionIndexByOptionId(
                oldNode,
                sourceOptionId,
              )
            }

            if (matchedOptionIndex !== undefined) {
              inferredSourceHandle = `option-${matchedOptionIndex}`
            }
          }

          if (oldNode.kind === 'transfer_rollover') {
            if (
              anchor === 'TopRight' &&
              (myLabel === 'No Answer' || myLabel === 'noAnswer')
            ) {
              inferredSourceHandle = 'noAnswer'
            } else if (
              anchor === 'BottomRight' &&
              (myLabel === 'Busy' || myLabel === 'busy')
            ) {
              inferredSourceHandle = 'busy'
            }
          }

          // Handle onTimeout/onInvalid pattern for queue, interactive_webhook, and auto_attendant
          if (
            oldNode.kind === 'queue' ||
            oldNode.kind === 'interactive_webhook' ||
            oldNode.kind === 'auto_attendant'
          ) {
            if (anchor === 'TopRight' && isOnTimeoutLabel(myLabel)) {
              inferredSourceHandle = 'onTimeout'
            } else if (
              oldNode.kind === 'queue' &&
              anchor === 'BottomRight' &&
              (myLabel === 'On Escape' || myLabel === 'onEscape')
            ) {
              inferredSourceHandle = 'onEscape'
            } else if (
              (oldNode.kind === 'interactive_webhook' ||
                oldNode.kind === 'auto_attendant') &&
              anchor === 'TopLeft' &&
              isOnInvalidLabel(myLabel)
            ) {
              inferredSourceHandle = 'onInvalid'
            }
          }

          // Handle option-* pattern for interactive_webhook and auto_attendant
          if (
            (oldNode.kind === 'interactive_webhook' ||
              oldNode.kind === 'auto_attendant') &&
            !isCustomHandleLowercase &&
            anchor === 'Right' &&
            !isOnInvalidLabel(myLabel) &&
            !isOnTimeoutLabel(myLabel)
          ) {
            if (hasMenuOptions(oldNode)) {
              let matchedOptionIndex: number | undefined
              const sourceOptionId =
                source.option_id ||
                extractOptionIdFromOptionSourceUuid(source.uuid)

              // CRITICAL: Try to match by option_id first (if preserved)
              if (sourceOptionId) {
                matchedOptionIndex = findOptionIndexByOptionId(
                  oldNode,
                  sourceOptionId,
                )
              }

              if (matchedOptionIndex !== undefined) {
                inferredSourceHandle = `option-${matchedOptionIndex}`
              }
            }
          }

          const isDynamicOptionEndpoint =
            anchor === 'Right' &&
            !isOnInvalidLabel(myLabel) &&
            !isOnTimeoutLabel(myLabel) &&
            !isCustomHandleLowercase &&
            (oldNode.kind === 'call_distribution' ||
              oldNode.kind === 'auto_attendant' ||
              oldNode.kind === 'interactive_webhook')

          // Fail-safe: never guess option mapping by position.
          // If we cannot derive the option handle semantically, preserve the
          // legacy endpoint metadata and skip ReactFlow edge creation.
          if (isDynamicOptionEndpoint && !inferredSourceHandle) {
            console.warn(
              '[Legacy Conversion] Skipping ambiguous dynamic option endpoint to prevent misrouting',
              {
                sourceNodeId: oldNode.id,
                sourceUuid: source.uuid,
                sourceLabel: myLabel,
                sourceAnchor: anchor,
                targetUuid: source.target_uuid,
                nodeKind: oldNode.kind,
              },
            )
            preserveSourceEndpointForRoundTrip(oldNode, source)
            return
          }

          // Handle ai_agent_v1 onError fixed handle
          if (
            oldNode.kind === 'ai_agent_v1' &&
            anchor === 'Right' &&
            (myLabel === 'On Error' || myLabel === 'onError')
          ) {
            inferredSourceHandle = 'onError'
          }

          // Handle schedule and day_night nodes (both use whenOpen/whenClosed)
          if (oldNode.kind === 'schedule' || oldNode.kind === 'day_night') {
            const whenOpenLabels = new Set([
              'Open',
              'open',
              'When Open',
              'whenOpen',
            ])
            const whenClosedLabels = new Set([
              'Closed',
              'closed',
              'When Closed',
              'When Closed.',
              'whenClosed',
            ])

            if (anchor === 'TopRight' && whenOpenLabels.has(myLabel)) {
              inferredSourceHandle = 'whenOpen'
            } else if (
              (oldNode.kind === 'schedule' && anchor === 'Right') ||
              (oldNode.kind === 'day_night' && anchor === 'BottomRight')
            ) {
              if (whenClosedLabels.has(myLabel)) {
                inferredSourceHandle = 'whenClosed'
              }
            } else if (
              oldNode.kind === 'schedule' &&
              anchor === 'BottomRight' &&
              (myLabel === 'Holiday' || myLabel === 'holiday')
            ) {
              inferredSourceHandle = 'holiday'
            }
          }

          const isCustomHandle =
            isCustomHandleLowercase || !!inferredSourceHandle

          const finalSourceHandle =
            inferredSourceHandle || (isCustomHandle ? myLabel : undefined)

          let finalEdgeId: string
          if (finalSourceHandle) {
            finalEdgeId = `${oldNode.id}-${finalSourceHandle}-${edgeTargetId}`
          } else if (isCustomHandle) {
            finalEdgeId = `${oldNode.id}-${myLabel}-${edgeTargetId}`
          } else {
            const sourceUuidSuffix = source.uuid
              ? source.uuid.split('_').pop() || source.uuid
              : ''
            finalEdgeId = `${oldNode.id}-${edgeTargetId}-${sourceUuidSuffix}`
          }

          if (!edgeIdSet.has(finalEdgeId)) {
            edgeIdSet.add(finalEdgeId)
            const edge: SwitchboardEdge = {
              id: finalEdgeId,
              source: oldNode.id,
              target: edgeTargetId,
              ...(finalSourceHandle && { sourceHandle: finalSourceHandle }),
              ...(!finalSourceHandle &&
                !isCustomHandle &&
                myLabel && { label: myLabel }),
            }
            edges.push(edge)
          }
        } else {
          preserveSourceEndpointForRoundTrip(oldNode, source)
        }
      }
    })
  })

  return edges
}

/**
 * Recreate missing option endpoints from children[] array
 *
 * This function addresses the issue where Legacy Admin's save logic filters out
 * option endpoints when connectionSource.sourceId != connectionSource.endpoints[0].anchor.elementId
 *
 * @param allLegacyNodes - Array of legacy format nodes
 * @param nodeMap - Map of node IDs to React Flow nodes (modified in place)
 */
/**
 * Recreate missing option endpoints from children[] array
 *
 * CRITICAL: This function ensures backend functionality works correctly
 *
 * BACKEND CONSUMER REQUIREMENT (canvasjson.lua):
 * Backend processes connections by matching: option['option_id'] == item.id
 * - auto_attendant (line 1000): option['option_id'] == item.id
 * - interactive_webhook (line 600): option['option_id'] == item.id
 * - call_distribution (line 1028): option['option_id'] == item.id
 * Backend NEEDS endpoints in sources array to process connections correctly
 * If endpoints are missing, backend cannot route calls through option menus
 *
 * LEGACY ADMIN ISSUE:
 * Legacy Admin's save condition may fail: connectionSource.sourceId == connectionSource.endpoints[0].anchor.elementId
 * This causes Legacy Admin to filter out option endpoints when saving
 * However, we can reconstruct connections from target endpoint references (source_uuid)
 *
 * SOLUTION:
 * 1. Recreate endpoints from children[] array (ensures all options have endpoints)
 * 2. Reconstruct connections from target endpoint references (preserves target_uuid)
 * 3. Store endpoints with target_uuid in unconnectedSourceEndpoints
 * 4. When Admin3 saves, endpoints are included in JSON (backend functionality works)
 * 5. When Legacy Admin saves, it may lose endpoints (but Admin3 reconstructs them on next load)
 *
 * RESULT:
 * - Backend functionality: Endpoints are in JSON when Admin3 saves, so backend can process connections
 * - Legacy Admin rendering: Endpoints are in JSON when Admin3 saves, so Legacy Admin can render them
 * - Round-trip compatibility: When Admin3 loads again, it reconstructs missing endpoints
 *
 * @param allLegacyNodes - Array of legacy format nodes
 * @param nodeMap - Map of node IDs to React Flow nodes (modified in place)
 */
export function recreateMissingOptionEndpoints(
  allLegacyNodes: LegacyFormatNode[],
  nodeMap: Map<string, SwitchboardNode>,
): void {
  // Use string type to avoid extra import (LegacyNodeKind would require import from '../shared/legacyNodes')
  const DYNAMIC_HANDLE_NODE_KINDS = new Set<string>([
    'call_distribution',
    'auto_attendant',
    'interactive_webhook',
  ])

  // CRITICAL: Build map of option endpoint UUIDs to target UUIDs by checking all target endpoints
  // When Legacy Admin loses option endpoints, target endpoints still reference them via source_uuid
  // We can reconstruct the connections from these references
  // Format: option endpoint UUID (${optionId}_src) -> target endpoint UUID
  //
  // BACKEND FUNCTIONALITY REQUIREMENT:
  // Backend needs endpoints with target_uuid to process connections correctly
  // Backend extracts node ID from target_uuid using: extract_id(target_uuid)
  // Backend then looks up the target node and routes the call to it
  // Without target_uuid, backend cannot route calls through option menus
  //
  // LEGACY ADMIN LIMITATION:
  // Legacy Admin's save condition may fail, causing endpoints to be lost
  // However, target endpoints still reference option endpoints via source_uuid
  // We can use these references to reconstruct the connections
  const optionUuidToTargetUuid = new Map<string, string>()
  allLegacyNodes.forEach((targetNode) => {
    const targets = Array.isArray(targetNode.endpoints?.targets)
      ? targetNode.endpoints.targets
      : []
    targets.forEach((target) => {
      if (target.source_uuid && typeof target.source_uuid === 'string') {
        const sourceUuid = String(target.source_uuid)
        // Check if this source_uuid matches an option endpoint UUID format: ${optionId}_src
        if (sourceUuid.endsWith('_src')) {
          const optionUuid = sourceUuid
          // Only map if we haven't seen this option UUID yet (first target wins)
          // This handles the case where multiple targets reference the same option endpoint
          if (!optionUuidToTargetUuid.has(optionUuid)) {
            optionUuidToTargetUuid.set(optionUuid, target.uuid || '')
          }
        }
      }
    })
  })

  allLegacyNodes.forEach((legacyNode) => {
    // Only process dynamic handle nodes
    if (!DYNAMIC_HANDLE_NODE_KINDS.has(legacyNode.kind)) {
      return
    }

    // Find ReactFlow node by matching numeric ID (legacy IDs are numeric, ReactFlow IDs are formatted)
    let reactFlowNode: SwitchboardNode | undefined = nodeMap.get(legacyNode.id)
    if (!reactFlowNode) {
      // Try to find by matching numeric ID part (e.g., '1234567890123' matches 'auto-attendant-1234567890123')
      for (const [nodeId, node] of nodeMap.entries()) {
        const nodeNumericId = nodeId.includes('-')
          ? nodeId.split('-').pop() || nodeId
          : nodeId
        if (nodeNumericId === legacyNode.id) {
          reactFlowNode = node
          break
        }
      }
    }

    if (!reactFlowNode || !reactFlowNode.data) {
      return
    }

    const html = legacyNode.html
    if (!html || typeof html !== 'object' || !Array.isArray(html.children)) {
      return
    }

    // Get existing option IDs from sources array (if any exist)
    const sources = Array.isArray(legacyNode.endpoints?.sources)
      ? legacyNode.endpoints.sources
      : []
    const existingOptionIds = new Set<string>()
    sources.forEach((source) => {
      if (source.option_id) {
        existingOptionIds.add(String(source.option_id))
      }
    })

    // Initialize unconnectedSourceEndpoints array if it doesn't exist
    if (!Array.isArray(reactFlowNode.data.unconnectedSourceEndpoints)) {
      reactFlowNode.data.unconnectedSourceEndpoints = []
    }

    // Also check existing unconnectedSourceEndpoints to avoid duplicates
    const existingUnconnectedEndpoints = Array.isArray(
      reactFlowNode.data.unconnectedSourceEndpoints,
    )
      ? reactFlowNode.data.unconnectedSourceEndpoints
      : []
    existingUnconnectedEndpoints.forEach((ep) => {
      if (ep.option_id && typeof ep.option_id === 'string') {
        existingOptionIds.add(String(ep.option_id))
      }
    })

    // Check each child (skip children[0] which is {})
    html.children.forEach((child, index) => {
      if (index === 0) return // Skip placeholder

      if (
        child &&
        typeof child === 'object' &&
        'id' in child &&
        typeof child.id === 'string' &&
        String(child.id).trim() !== ''
      ) {
        const optionId = String(child.id)
        const optionUuid = `${optionId}_src`

        // If this option doesn't have an endpoint, recreate it
        if (!existingOptionIds.has(optionId)) {
          // CRITICAL: Check if we can reconstruct the connection from target endpoints
          // If a target endpoint references this option endpoint UUID, we can recreate the connection
          const targetUuid = optionUuidToTargetUuid.get(optionUuid)

          const endpoint: Record<string, unknown> = {
            uuid: optionUuid, // Match format from toLegacy/index.ts: ${optionId}_src
            anchor: 'Right',
            my_label: 'Drag',
            // CRITICAL: option_id MUST match children[].id exactly
            // Legacy Admin creates DOM element with id = children[].id
            // Legacy Admin attaches jsPlumb endpoint to that element using option_id
            // Legacy Admin creates connection with sourceId = option_id
            // Legacy Admin saves connection only if: sourceId == anchor.elementId
            // Therefore: option_id must equal children[].id for the condition to pass
            option_id: optionId, // CRITICAL: Use children[].id (matches backend consumer and Legacy Admin requirement)
          }

          // CRITICAL: If we found a target UUID, include it so Admin3 can create the edge when saving
          // This ensures the endpoint is saved as CONNECTED, not unconnected
          // Backend requirement: Backend needs target_uuid to extract target node ID for routing
          // Backend uses: extract_id(target_uuid) to get target node ID
          // Without target_uuid, backend cannot route calls through option menus
          // Legacy Admin: Legacy Admin can create jsPlumb connections for connected endpoints
          // Even if Legacy Admin fails to save them, endpoints are in JSON when Admin3 saves
          // This ensures backend functionality works correctly regardless of Legacy Admin save behavior
          if (targetUuid) {
            endpoint.target_uuid = targetUuid
          }

          // Check if already in unconnectedSourceEndpoints (avoid duplicates)
          const existing = (
            reactFlowNode.data.unconnectedSourceEndpoints as Array<
              Record<string, unknown>
            >
          ).find((ep) => ep.uuid === endpoint.uuid || ep.option_id === optionId)

          if (!existing) {
            ;(
              reactFlowNode.data.unconnectedSourceEndpoints as Array<
                Record<string, unknown>
              >
            ).push(endpoint)
          }
        }
      }
    })
  })
}

/**
 * Check if a label matches "On Invalid" (case-insensitive)
 * @param label - Label to check
 * @returns True if label matches "On Invalid" or "onInvalid"
 */
function isOnInvalidLabel(label: string): boolean {
  return label === 'On Invalid' || label === 'onInvalid'
}

/**
 * Custom handle labels that should be treated as lowercase identifiers
 */
const CUSTOM_HANDLE_LABELS = new Set([
  'option-',
  'onInvalid',
  'onTimeout',
  'onError',
  'whenOpen',
  'whenClosed',
  'holiday',
  'onEscape',
  'noAnswer',
  'busy',
])

/**
 * Check if a label matches "On Timeout" (case-insensitive)
 * @param label - Label to check
 * @returns True if label matches "On Timeout" or "onTimeout"
 */
function isOnTimeoutLabel(label: string): boolean {
  return label === 'On Timeout' || label === 'onTimeout'
}

/**
 * Helper to get field value from nodeData, checking both camelCase and snake_case variants
 * @param nodeData - Node data object
 * @param camelCaseField - Camel case field name
 * @param snakeCaseField - Snake case field name
 * @returns Field value or undefined
 */
function getFieldValue(
  nodeData: Record<string, unknown>,
  camelCaseField: string,
  snakeCaseField: string,
): unknown {
  return nodeData[camelCaseField] || nodeData[snakeCaseField]
}

/**
 * Create edge from field with fallback to snake_case variant
 * @param sourceNode - Source node
 * @param nodeData - Node data object
 * @param camelCaseField - Camel case field name
 * @param snakeCaseField - Snake case field name
 * @param sourceHandle - Source handle identifier
 * @param nodeMap - Map of node IDs to React Flow nodes
 * @param edges - Array of existing edges
 */
function createEdgeFromFieldWithFallback(
  sourceNode: SwitchboardNode,
  nodeData: Record<string, unknown>,
  camelCaseField: string,
  snakeCaseField: string,
  sourceHandle: string,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const targetValue = getFieldValue(nodeData, camelCaseField, snakeCaseField)
  createEdgeFromField(sourceNode, targetValue, sourceHandle, nodeMap, edges)
}

/**
 * Check if a node has menu options (children array with more than just empty object)
 * @param oldNode - Legacy format node
 * @returns True if node has options
 */
function hasMenuOptions(oldNode: LegacyFormatNode): boolean {
  return (
    !!oldNode.html?.children &&
    Array.isArray(oldNode.html.children) &&
    oldNode.html.children.length > 1
  )
}

/**
 * Find option index by matching option_id to children[].id
 *
 * CRITICAL: ALL node types (auto_attendant, interactive_webhook, call_distribution)
 * match option_id against children[].id, NOT children[].option_id
 *
 * Backend consumer requirements (verified in canvasjson.lua):
 * - auto_attendant (line 173): option['option_id'] == item.id
 * - interactive_webhook (line 600): option['option_id'] == item.id (NOT item.option_id)
 * - call_distribution (line 751): option['option_id'] == item.id
 *
 * Note: interactive_webhook uses item.option_id only as press_id VALUE, not for matching
 *
 * @param oldNode - Legacy format node with children array
 * @param optionId - The option_id from source endpoint
 * @returns The option index (0-based, after skipping children[0]) or undefined if not found
 */
function findOptionIndexByOptionId(
  oldNode: LegacyFormatNode,
  optionId: string,
): number | undefined {
  if (!oldNode.html?.children || !Array.isArray(oldNode.html.children)) {
    return undefined
  }

  // Find option by matching option_id to children[].id (skip children[0])
  for (let i = 1; i < oldNode.html.children.length; i++) {
    const child = oldNode.html.children[i] as Record<string, unknown>
    if (child.id === optionId) {
      // Found matching option - return index (0-based, after skipping children[0])
      return i - 1
    }
  }

  return undefined
}

/**
 * Find a target node by ID, handling both original IDs and numeric IDs after round-trip conversion
 * @param targetId - The target node ID (may be original like 'sound-1' or numeric like '1')
 * @param nodeMap - Map of node IDs to React Flow nodes
 * @returns The target node if found, undefined otherwise
 */
function findTargetNode(
  targetId: unknown,
  nodeMap: Map<string, SwitchboardNode>,
): SwitchboardNode | undefined {
  if (!targetId) return undefined

  const targetIdStr = String(targetId)
  // First, try direct lookup
  const targetNode = nodeMap.get(targetIdStr)
  if (targetNode) return targetNode

  // If not found, try extracting numeric ID and matching
  const numericId = targetIdStr.includes('-')
    ? targetIdStr.split('-').pop()
    : targetIdStr

  if (numericId) {
    // Try to find node by numeric ID
    for (const [nodeId, n] of nodeMap.entries()) {
      const nodeNumericId = nodeId.includes('-')
        ? nodeId.split('-').pop()
        : nodeId
      if (nodeNumericId === numericId) {
        return n
      }
    }
  }

  return undefined
}

/**
 * Create or update an edge from a source node to a target node
 * @param sourceNodeId - ID of the source node
 * @param targetNode - Target node
 * @param sourceHandle - Source handle identifier (e.g., 'whenOpen', 'option-0')
 * @param edges - Array of existing edges (will be mutated)
 * @returns The created or updated edge
 */
function createOrUpdateEdge(
  sourceNodeId: string,
  targetNode: SwitchboardNode,
  sourceHandle: string,
  edges: SwitchboardEdge[],
): SwitchboardEdge | null {
  const edgeId = `${sourceNodeId}-${sourceHandle}-${targetNode.id}`

  const existingEdge = edges.find((e) => e.id === edgeId)
  if (existingEdge) {
    if (existingEdge.sourceHandle !== sourceHandle) {
      existingEdge.sourceHandle = sourceHandle
    }
    return existingEdge
  }

  // Only reuse unresolved edges that don't have a handle yet.
  // Different source handles pointing to the same target are valid
  // (e.g. multiple IVR options can intentionally route to one node).
  const conflictingEdge = edges.find(
    (e) =>
      e.source === sourceNodeId &&
      e.target === targetNode.id &&
      !e.sourceHandle,
  )

  if (conflictingEdge) {
    conflictingEdge.sourceHandle = sourceHandle
    conflictingEdge.id = edgeId
    return conflictingEdge
  }

  const edge: SwitchboardEdge = {
    id: edgeId,
    source: sourceNodeId,
    target: targetNode.id,
    sourceHandle,
    type: 'default',
  }
  edges.push(edge)
  return edge
}

/**
 * Create an edge from a node data field to a target node
 * Handles both direct lookup and numeric ID matching for round-trip conversions
 * @param sourceNode - Source node
 * @param targetValue - Target value from node data (may be original ID or numeric ID)
 * @param sourceHandle - Source handle identifier
 * @param nodeMap - Map of node IDs to React Flow nodes
 * @param edges - Array of existing edges (will be mutated)
 */
function createEdgeFromField(
  sourceNode: SwitchboardNode,
  targetValue: unknown,
  sourceHandle: string,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  if (!targetValue) return

  const targetNode = findTargetNode(targetValue, nodeMap)
  if (targetNode) {
    createOrUpdateEdge(sourceNode.id, targetNode, sourceHandle, edges)
  }
}

/**
 * Handle option-* fields for nodes with dynamic handles (call_distribution, interactive_webhook)
 * @param node - Source node
 * @param nodeData - Node data object
 * @param nodeMap - Map of node IDs to React Flow nodes
 * @param edges - Array of existing edges (will be mutated)
 */
function handleOptionFields(
  node: SwitchboardNode,
  nodeData: Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const optionKeys = Object.keys(nodeData).filter((key) =>
    key.startsWith('option-'),
  )

  optionKeys.forEach((key) => {
    const targetValue = nodeData[key]
    if (targetValue) {
      const targetNode = nodeMap.get(String(targetValue))
      if (targetNode) {
        createOrUpdateEdge(node.id, targetNode, key, edges)
      }
    }
  })
}

/**
 * Create edges from custom handle fields in node data
 *
 * Handles connections stored in node data fields (e.g., option-0, onInvalid, whenOpen)
 * that may not have been captured in the legacy endpoints.
 *
 * @param nodes - Array of React Flow nodes
 * @param edges - Array of existing edges (will be mutated to add new edges)
 * @param nodeMap - Map of node IDs to React Flow nodes
 */
export const createEdgesFromNodeData = (
  nodes: SwitchboardNode[],
  edges: SwitchboardEdge[],
  nodeMap: Map<string, SwitchboardNode>,
): void => {
  // Handle Auto Attendant option connections (option-0, option-1, etc.) and onInvalid/onTimeout
  nodes.forEach((node) => {
    if (node.type === 'auto_attendant' && node.data) {
      const nodeData = node.data as AutoAttendantNodeData &
        Record<string, unknown>

      handleOptionFields(node, nodeData, nodeMap, edges)

      const existingOnInvalidEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'onInvalid',
      )

      if (nodeData.onInvalid) {
        const onInvalidTarget = String(nodeData.onInvalid)
        const targetNode = nodeMap.get(onInvalidTarget)

        if (targetNode) {
          const edgeId = `${node.id}-onInvalid-${targetNode.id}`

          if (
            onInvalidTarget === node.id &&
            existingOnInvalidEdge &&
            existingOnInvalidEdge.target !== node.id
          ) {
            existingOnInvalidEdge.target = node.id
            existingOnInvalidEdge.id = edgeId
            existingOnInvalidEdge.sourceHandle = 'onInvalid'
          } else if (existingOnInvalidEdge) {
            if (existingOnInvalidEdge.sourceHandle !== 'onInvalid') {
              existingOnInvalidEdge.sourceHandle = 'onInvalid'
            }
          } else {
            createOrUpdateEdge(node.id, targetNode, 'onInvalid', edges)
          }
        }
      }
      if (nodeData.onTimeout) {
        const targetNode = nodeMap.get(String(nodeData.onTimeout))
        if (targetNode) {
          createOrUpdateEdge(node.id, targetNode, 'onTimeout', edges)
        }
      }
    }

    if (node.type === NodeType.Schedule && node.data) {
      const nodeData = node.data as ScheduleNodeData & Record<string, unknown>

      createEdgeFromFieldWithFallback(
        node,
        nodeData,
        'whenOpen',
        'when_open',
        'whenOpen',
        nodeMap,
        edges,
      )
      createEdgeFromFieldWithFallback(
        node,
        nodeData,
        'whenClosed',
        'when_closed',
        'whenClosed',
        nodeMap,
        edges,
      )
      createEdgeFromField(node, nodeData.holiday, 'holiday', nodeMap, edges)
    }

    if (node.type === NodeType.DayNight && node.data) {
      const nodeData = node.data as DayNightNodeData & Record<string, unknown>

      const whenOpenValue =
        getFieldValue(nodeData, 'whenOpen', 'when_open') ||
        getFieldValue(nodeData, 'dayPath', 'day_path')
      createEdgeFromField(node, whenOpenValue, 'whenOpen', nodeMap, edges)

      const whenClosedValue =
        getFieldValue(nodeData, 'whenClosed', 'when_closed') ||
        getFieldValue(nodeData, 'nightPath', 'night_path')
      createEdgeFromField(node, whenClosedValue, 'whenClosed', nodeMap, edges)
    }

    if (node.type === 'queue' && node.data) {
      const nodeData = node.data as QueueNodeData & Record<string, unknown>

      if (nodeData.onTimeout && typeof nodeData.onTimeout === 'string') {
        createEdgeFromField(
          node,
          nodeData.onTimeout,
          'onTimeout',
          nodeMap,
          edges,
        )
      }

      createEdgeFromFieldWithFallback(
        node,
        nodeData,
        'onEscape',
        'on_escape',
        'onEscape',
        nodeMap,
        edges,
      )
    }

    if (node.type === 'transfer_and_rollover' && node.data) {
      const nodeData = node.data as Record<string, unknown>

      createEdgeFromFieldWithFallback(
        node,
        nodeData,
        'noAnswer',
        'no_answer',
        'noAnswer',
        nodeMap,
        edges,
      )
      createEdgeFromField(node, nodeData.busy, 'busy', nodeMap, edges)
    }

    if (node.type === 'call_distribution' && node.data) {
      const nodeData = node.data as CallDistributionNodeData &
        Record<string, unknown>

      handleOptionFields(node, nodeData, nodeMap, edges)
    }

    if (node.type === 'interactive_webhook' && node.data) {
      const nodeData = node.data as InteractiveWebhookNodeData &
        Record<string, unknown>

      handleOptionFields(node, nodeData, nodeMap, edges)
      createEdgeFromField(node, nodeData.onInvalid, 'onInvalid', nodeMap, edges)
      createEdgeFromFieldWithFallback(
        node,
        nodeData,
        'onTimeout',
        'on_timeout',
        'onTimeout',
        nodeMap,
        edges,
      )
    }
  })
}
